import React, { useEffect, useState } from 'react';
import {
  Badge,
  Button,
  Card,
  Col,
  Form,
  InputGroup,
  Modal,
  OverlayTrigger,
  Row,
  Spinner,
  Stack,
  Table,
  Tooltip,
} from 'react-bootstrap';
import * as XLSX from 'xlsx';
import rest from '../axios';
import PrestadorForm from './PrestadorForm';

import 'bootstrap-icons/font/bootstrap-icons.css';
import 'bootstrap/dist/css/bootstrap.min.css';
import './css/prestadores.css';

const initialPrestador = {
  name: '',
  phone: '',
  email: '',
  category_id: '',
  subcategory_id: '',
  city_id: '',
  neighborhood_id: '',
  description: '',
  website: '',
  instagram: '',
  cpf_cnpj: '',
  community_id: '',
  morar_no_condominio: false,
  apartment: '',
  tower: '',
  atende_em_casa: false,
  data_de_inicio: '',
  created_by: '',
  has_connexi_seal: false,
  sponsored: false,
};

export default function PrestadoresComponent() {
  const [prestadores, setPrestadores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showAdd, setShowAdd] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [selected, setSelected] = useState(initialPrestador);

  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [cities, setCities] = useState([]);
  const [neighborhoods, setNeighborhoods] = useState([]);
  const [communities, setCommunities] = useState([]);

  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  useEffect(() => {
    const endpoints = [
      ['/admin/providers', setPrestadores],
      ['/admin/categories', (data) => {
        const uniqueCategories = Array.from(new Set(data.map((item) => item.id)))
          .map((id) => data.find((item) => item.id === id));
        setCategories(uniqueCategories);
      }],
      ['/cities', setCities],
    ];

    Promise.allSettled(
      endpoints.map(([url, handler]) =>
        rest.get(url).then((response) => handler(response.data?.data))
      )
    ).then((res) => {
      res.forEach((r, i) => {
        if (r.status !== 'fulfilled') {
          console.warn(`Falhou ${endpoints[i][0]}`, r.reason?.response?.status);
        }
      });
      setLoading(false);
    });
  }, []);

  useEffect(() => {
    if (!selected.city_id) {
      setNeighborhoods([]);
      setCommunities([]);
      return;
    }

    const cityId = Number(selected.city_id);

    Promise.allSettled([
      rest.get(`/neighborhoods/city/${cityId}`),
      rest.get(`/admin/communities`),
    ]).then(([nRes, cRes]) => {
      if (nRes.status === 'fulfilled') setNeighborhoods(nRes.value.data);
      if (cRes.status === 'fulfilled') setCommunities(cRes.value.data?.data?.communities);
    });
  }, [selected.city_id]);

  useEffect(() => {
    if (!selected.category_id) {
      setSubcategories([]);
      return;
    }

    rest
      .get(`/admin/subcategories/category/${selected.category_id}`)
      .then((res) => setSubcategories(res.data?.data))
      .catch((err) => {
        console.error('Erro ao carregar subcategorias', err);
        setSubcategories([]);
      });
  }, [selected.category_id]);

  const refresh = () => rest.get('/admin/providers').then((r) => setPrestadores(r.data?.data));

  const handleCreate = () => {
    const payload = {
      name: selected.name,
      phone: selected.phone,
      ...(selected.email && { email: selected.email }),
      categoryId: Number(selected.category_id),
      subcategoryId: Number(selected.subcategory_id),
      description: selected.description,
      apartment: selected.apartment,
      tower: selected.tower,
      website: selected.website,
      instagram: selected.instagram,
      ...(selected.cpf_cnpj && { cpfCnpj: Number(selected.cpf_cnpj) }),
      cityId: Number(selected.city_id),
      neighborhoodId: Number(selected.neighborhood_id),
      communityId: Number(selected.community_id),
      createdBy: selected.created_by,
      atendeEmCasa: selected.atende_em_casa,
      dataDeInicio: selected.data_de_inicio ? new Date(selected.data_de_inicio) : new Date(),
      hasConnexiSeal: selected.has_connexi_seal ?? false,
      sponsored: selected.sponsored ?? false,
    };

    rest.post('/admin/providers', payload).then(() => {
      setShowAdd(false);
      setSelected(initialPrestador);
      refresh();
    });
  };

  const handleUpdate = () => {
    const payload = {
      name: selected.name,
      phone: selected.phone,
      ...(selected.email && { email: selected.email }),
      description: selected.description ?? '',
      apartment: selected.apartment ?? '',
      tower: selected.tower ?? '',
      website: selected.website ?? '',
      instagram: selected.instagram ?? '',
      ...(selected.cpf_cnpj && { cpf_cnpj: Number(selected.cpf_cnpj) }),
      city_id: selected.city_id ? Number(selected.city_id) : null,
      neighborhood_id: selected.neighborhood_id ? Number(selected.neighborhood_id) : null,
      community_id: selected.community_id ? Number(selected.community_id) : null,
      category_id: selected.category_id ? Number(selected.category_id) : null,
      subcategory_id: selected.subcategory_id ? Number(selected.subcategory_id) : null,
      atende_em_casa: selected.atende_em_casa ?? false,
      data_de_inicio: selected.data_de_inicio ? new Date(selected.data_de_inicio) : null,
      created_by: selected.created_by,
      hasConnexiSeal: selected.has_connexi_seal ?? false,
      sponsored: selected.sponsored ?? false,
    };

    rest.put(`/admin/providers/${selected.id}`, payload).then(() => {
      setShowEdit(false);
      setSelected(initialPrestador);
      refresh();
    });
  };

  const handleRemove = () =>
    rest.delete(`/admin/providers/${selected.id}`).then(() => {
      setShowDelete(false);
      refresh();
    });

  const handleSearch = (txt) => {
    setSearch(txt);
    if (!txt) return refresh();
    setPrestadores((prev) =>
      prev.filter((p) =>
        [p.name, p.category_name, p.subcategory_name, p.phone]
          .filter(Boolean)
          .some((f) => f.toLowerCase().includes(txt.toLowerCase()))
      )
    );
  };

  const handleDateFilter = () => {
    let filtered = prestadores;

    if (startDate) {
      filtered = filtered.filter((p) => new Date(p.created_at) >= new Date(startDate));
    }
    if (endDate) {
      filtered = filtered.filter((p) => new Date(p.created_at) <= new Date(endDate));
    }

    setPrestadores(filtered);
  };

  const exportToExcel = () => {
    const ws = XLSX.utils.json_to_sheet(prestadores);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Prestadores');
    XLSX.writeFile(wb, 'prestadores.xlsx');
  };

  return (
    <div className="p-4">
      <Card className="shadow-sm border-0">
        <Card.Header className="bg-white border-0">
          <Row className="toolbar align-items-center">
            <Col xs={12} md="auto">
              <h5 className="card-title mb-0">
                <i className="bi bi-person-badge" /> Prestadores{' '}
                <Badge bg="secondary" pill>
                  {prestadores.length}
                </Badge>
              </h5>
            </Col>

            <Col className="flex-grow-1 mt-2 mt-md-0">
              <InputGroup>
                <InputGroup.Text>
                  <i className="bi bi-search" />
                </InputGroup.Text>
                <Form.Control
                  placeholder="Buscar nome, categoria ou telefone…"
                  value={search}
                  onChange={(e) => handleSearch(e.target.value)}
                />
              </InputGroup>
            </Col>

            <Col xs={12} md="auto" className="text-md-end mt-2 mt-md-0">
              <Button onClick={() => { setSelected(initialPrestador); setShowAdd(true); }}>
                <i className="bi bi-plus-circle me-2" /> Adicionar prestadores
              </Button>
              <Button onClick={exportToExcel} variant="outline-success" className="ms-2">
                <i className="bi bi-file-earmark-excel me-2" /> Exportar Excel
              </Button>
            </Col>
          </Row>

          <Row className="mt-2">
            <Col md={5}>
              <Form.Group>
                <Form.Label>Data Inicial</Form.Label>
                <Form.Control type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
              </Form.Group>
            </Col>
            <Col md={5}>
              <Form.Group>
                <Form.Label>Data Final</Form.Label>
                <Form.Control type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
              </Form.Group>
            </Col>
            <Col md={2}>
              <Button onClick={handleDateFilter} className="mt-4">Filtrar</Button>
            </Col>
          </Row>
        </Card.Header>

        <Card.Body className="p-0">
          {loading ? (
            <div className="py-5 text-center">
              <Spinner animation="border" />
            </div>
          ) : (
            <div className="table-responsive">
              <Table hover striped className="mb-0">
                <thead>
                  <tr>
                    <th>Nome</th>
                    <th>Categoria</th>
                    <th>Subcategoria</th>
                    <th>Telefone</th>
                    <th>Cidade</th>
                    <th>Bairro</th>
                    <th>Condomínio</th>
                    <th className="text-center">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {prestadores.length === 0 ? (
                    <tr>
                      <td colSpan={8} className="py-4 text-center">
                        Nenhum prestador encontrado
                      </td>
                    </tr>
                  ) : (
                    prestadores.map((p) => (
                      <tr key={p.id}>
                        <td>{p.name}</td>
                        <td>{p?.category?.name || '—'}</td>
                        <td>{p?.subcategory?.name || '—'}</td>
                        <td>{p?.phone || '—'}</td>
                        <td>{p?.city?.name || '—'}</td>
                        <td>{p?.neighborhood?.name || '—'}</td>
                        <td>{p?.community?.name || '—'}</td>
                        <td className="text-center">
                          <Stack direction="horizontal" gap={2} className="justify-content-center">
                            <OverlayTrigger placement="top" overlay={<Tooltip>Editar</Tooltip>}>
                              <Button size="sm" variant="outline-secondary" onClick={() => { setSelected(p); setShowEdit(true); }}>
                                <i className="bi bi-pencil" />
                              </Button>
                            </OverlayTrigger>
                            <OverlayTrigger placement="top" overlay={<Tooltip>Excluir</Tooltip>}>
                              <Button size="sm" variant="outline-danger" onClick={() => { setSelected(p); setShowDelete(true); }}>
                                <i className="bi bi-trash" />
                              </Button>
                            </OverlayTrigger>
                          </Stack>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </Table>
            </div>
          )}
        </Card.Body>
      </Card>

      {/* ---------- MODAIS ---------- */}
      <Modal show={showAdd} onHide={() => setShowAdd(false)} size="lg" centered backdrop="static">
        <Modal.Header closeButton><Modal.Title>Novo Prestador</Modal.Title></Modal.Header>
        <Modal.Body>
          <PrestadorForm
            formData={selected}
            setFormData={setSelected}
            categories={categories}
            subcategories={subcategories}
            cities={cities}
            neighborhoods={neighborhoods}
            communities={communities}
          />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowAdd(false)}>Cancelar</Button>
          <Button variant="primary" onClick={handleCreate}>Salvar</Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showEdit} onHide={() => setShowEdit(false)} size="lg" centered backdrop="static">
        <Modal.Header closeButton><Modal.Title>Editar Prestador</Modal.Title></Modal.Header>
        <Modal.Body>
          <PrestadorForm
            formData={selected}
            setFormData={setSelected}
            categories={categories}
            subcategories={subcategories}
            cities={cities}
            neighborhoods={neighborhoods}
            communities={communities}
          />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowEdit(false)}>Cancelar</Button>
          <Button variant="primary" onClick={handleUpdate}>Salvar Alterações</Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showDelete} onHide={() => setShowDelete(false)} centered backdrop="static">
        <Modal.Header closeButton><Modal.Title>Confirmar exclusão</Modal.Title></Modal.Header>
        <Modal.Body>Tem certeza de que deseja excluir este prestador?</Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDelete(false)}>Cancelar</Button>
          <Button variant="danger" onClick={handleRemove}>Excluir</Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
